import { useEffect } from 'react';
import { TextStyled, Flex, StyledImg } from '../../component-library';
import { usePostMessage } from '../../context/messageContext';
import { getLogmarLineSize500mm } from '../../helpers/functions';
import { logmarData } from '../../helpers/data';
import { useCalibration } from '../../context/calibrationContext';
import { calibrationConstants } from '../../helpers/calibration-constants';
import LandoltCSVG from '../../assets/img/landolt-c.svg';
import WholeCircleSVG from '../../assets/img/whole-circle.svg';
import { useBreakpoints } from '../../helpers/useBreakpoints';

const LandoltC = ({ opticianView, logmarSize, openingQuadrant }) => {
  const { breakpoints } = useBreakpoints();
  const postMessage = usePostMessage();
  const { patientDistanceReference, blindSpotThumbnailWidthPx } = calibrationConstants;
  const {
    calibrationData: { pixelDensity, distanceFromScreen },
  } = useCalibration();

  const rotationDegreesByQuadrant = {
    1: -135,
    2: -45,
    3: 135,
    4: 45,
  };

  const getPxSizeFromLogmar = () => {
    const getLogmarContentSize = (logmar) => {
      const logmarDataObject = logmarData.find((logmarObject) => {
        return logmarObject.value === +logmar.toFixed(1);
      });

      const lineSize = getLogmarLineSize500mm(logmarDataObject.id);
      const contentSize = (lineSize * distanceFromScreen) / patientDistanceReference / pixelDensity;
      return contentSize;
    };

    const finalContentSize = Math.round(getLogmarContentSize(logmarSize));
    return finalContentSize;
  };

  const styles = {
    container: {
      position: 'relative',
      height: logmarSize ? '100px' : opticianView ? breakpoints('100px', '200px', '300px') : '100%',
      width: logmarSize ? '150px' : opticianView ? breakpoints('200px', '350px', '500px') : '100%',
      minWidth: '125px',
      minHeight: '125px',
      maxWidth: opticianView ? '700px' : '1400px',
      maxHeight: opticianView ? '300px' : '1000px',
    },
    quadrantIndicators: {
      borderRadius: '20%',
      justifyContent: 'center',
      alignItems: 'center',
      border: '1px solid black',
      height: logmarSize ? '40px' : opticianView ? breakpoints('35px', '55px', '75px') : '175px',
      width: logmarSize ? '40px' : opticianView ? breakpoints('35px', '55px', '75px') : '175px',
    },
    landoltC: {
      minWidth: '40px',
      transform: `rotate(${rotationDegreesByQuadrant[openingQuadrant] || 0}deg)`,
    },
  };

  const QuadrantIndicators = () => {
    const quandrantColors = {
      1: '#ffff00',
      2: '#ff0000',
      3: '#002060',
      4: '#06b050',
    };
    return [1, 2, 3, 4].map((quadrant) => {
      return (
        <Flex
          style={{
            ...styles.quadrantIndicators,
            position: 'absolute',
            top: quadrant < 3 ? 0 : `calc(100% - ${styles.quadrantIndicators.height})`,
            left: quadrant % 2 ? 0 : `calc(100% - ${styles.quadrantIndicators.width})`,
          }}
          bg={quandrantColors[quadrant]}
          key={`landolt-C-quadrant-number-${quadrant}`}
        >
          <TextStyled
            fontWeight='700'
            color={quadrant < 2 ? 'black' : 'white'}
            fontSize={logmarSize ? '1rem' : opticianView ? breakpoints('0.825rem', '1rem', '2rem') : '6rem'}
          >
            {quadrant}
          </TextStyled>
        </Flex>
      );
    });
  };

  useEffect(() => {
    if (!logmarSize && !opticianView) {
      const handleKeyboardInput = (e) => {
        if (+e.key > 0 && +e.key < 5) {
          postMessage({ type: 'patientQuadrantResponse', content: +e.key });
        }
      };
      window.addEventListener('keydown', handleKeyboardInput);
      return () => window.removeEventListener('keydown', handleKeyboardInput);
    }
  }, [postMessage, opticianView, logmarSize]);

  return (
    <Flex style={styles.container} justifyContent='center' alignItems='center' p='2rem'>
      <QuadrantIndicators />
      <Flex
        width={`calc(${styles.container.width} - ${styles.quadrantIndicators.width} * 2)`}
        height={`calc(${styles.container.height} - ${styles.quadrantIndicators.height})`}
        alignItems='center'
        justifyContent='center'
      >
        <StyledImg
          width={logmarSize ? getPxSizeFromLogmar() : opticianView ? '75%' : blindSpotThumbnailWidthPx * 2.25}
          height={logmarSize ? getPxSizeFromLogmar() : 'auto'}
          src={openingQuadrant ? LandoltCSVG : WholeCircleSVG}
          style={styles.landoltC}
        />
      </Flex>
    </Flex>
  );
};

export default LandoltC;
